import {BankAccountIdentifierType, TransferType} from '@sollapay/enums';
import {
  composeIsraeliIban,
  isValidIsraeliBankAccountNumber,
  isValidIsraeliBankCode,
  isValidIsraeliBranchCode,
  parseIsraeliIban
} from '@sollapay/utils';

import {findBankProviderByBankCode} from './creditor-account.utils';

import type {BankAccountInputType} from './creditor-account.utils';
import type {BankTransferType, PaymentProviderDTO} from '@sollapay/types';

export interface BankDetailsValues {
  bankProviderId: string;
  bankCode: string;
  branchCode: string;
  bankAccountNumber: string;
}

/** The account-type select doubles as the rail picker: IBAN input clears via zahav, bank details via masav. */
export function resolvePayoutTransferType(accountType: BankAccountInputType): BankTransferType {
  return accountType === BankAccountIdentifierType.IBAN ? TransferType.ZAHAV : TransferType.MASAV;
}

/** Composes the entered bank/branch/account into an IBAN, or `null` when any part is missing or malformed. */
export function bankDetailsToIban(
  values: Pick<BankDetailsValues, 'bankCode' | 'branchCode' | 'bankAccountNumber'>
): string | null {
  const {bankCode, branchCode, bankAccountNumber} = values;
  if (
    !isValidIsraeliBankCode(bankCode) ||
    !isValidIsraeliBranchCode(branchCode) ||
    !isValidIsraeliBankAccountNumber(bankAccountNumber)
  ) {
    return null;
  }
  return composeIsraeliIban({bankCode, branchCode, accountNumber: bankAccountNumber});
}

/** Splits an Israeli IBAN into the bank-details fields, or `null` when it isn't a valid Israeli IBAN. */
export function ibanToBankDetails(
  iban: string,
  bankProviders: PaymentProviderDTO[]
): BankDetailsValues | null {
  const parts = parseIsraeliIban(iban);
  if (!parts) return null;

  return {
    bankProviderId: findBankProviderByBankCode(bankProviders, parts.bankCode)?.id ?? '',
    bankCode: parts.bankCode,
    branchCode: parts.branchCode,
    bankAccountNumber: parts.accountNumber
  };
}
